// src/services/platformStatsService.js
import { 
  collection, 
  getCountFromServer,
  query, 
  where 
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Get real platform-wide statistics
 * @returns {Promise<Object>} - Platform statistics
 */
export const getPlatformStats = async () => {
  try {
    // Get total students
    const studentsCountSnap = await getCountFromServer(collection(db, 'studentProfiles'));
    const totalStudents = studentsCountSnap.data().count;

    // Get total companies
    const companiesCountSnap = await getCountFromServer(collection(db, 'companyProfiles'));
    const totalCompanies = companiesCountSnap.data().count;

    // Get total projects
    const projectsCountSnap = await getCountFromServer(collection(db, 'projects'));
    const totalProjects = projectsCountSnap.data().count;

    // Completed projects
    const completedProjectsQuery = query(collection(db, 'projects'), where('status', '==', 'completed'));
    const completedCountSnap = await getCountFromServer(completedProjectsQuery);
    const completedProjects = completedCountSnap.data().count;

    // Active projects
    const activeProjectsQuery = query(collection(db, 'projects'), where('status', '==', 'open'));
    const activeCountSnap = await getCountFromServer(activeProjectsQuery);
    const activeProjects = activeCountSnap.data().count;

    return {
      totalStudents,
      totalCompanies,
      totalProjects,
      completedProjects,
      activeProjects,
      lastUpdated: new Date().toISOString()
    };

  } catch (error) {
    console.error('Error fetching platform stats:', error);
    // Return minimal stats if error occurs
    return {
      totalStudents: 0,
      totalCompanies: 0,
      totalProjects: 0,
      completedProjects: 0,
      activeProjects: 0,
      lastUpdated: new Date().toISOString()
    };
  }
};

/**
 * Get cached stats with fallback to real-time fetch
 * Uses a simple in-memory cache for 5 minutes
 */
let statsCache = null;
let lastFetch = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getCachedPlatformStats = async () => {
  const now = Date.now();
  
  if (statsCache && lastFetch && (now - lastFetch) < CACHE_DURATION) {
    // If previous fetch produced all zeros (likely due to permissions / first load), bypass cache
    const { totalStudents, totalCompanies, totalProjects } = statsCache;
    if (totalStudents === 0 && totalCompanies === 0 && totalProjects === 0) {
      // fall through to refetch
    } else {
      return statsCache;
    }
  }

  const stats = await getPlatformStats();
  statsCache = stats;
  lastFetch = now;
  
  return stats;
}; 